import {useMemo, useState} from "react";
import {Alert, Radio, Typography} from "antd";
import {t} from "i18next";
import {parseHl7} from "./hl7Parser";

const isBase64ish = (v) => !!v && /^[A-Za-z0-9+/=\s]+$/.test(v) && v.replace(/\s+/g, '').length > 80;

const decodeBase64Utf8 = (b64) => {
    const clean = (b64 || '').replace(/\s+/g, '');
    const bin = atob(clean);
    try {
        const bytes = Uint8Array.from(bin, c => c.charCodeAt(0));
        return new TextDecoder('utf-8').decode(bytes);
    } catch {
        return bin;
    }
};

const detectKind = (s) => {
    const head = (s || '').trimStart();
    if (head.startsWith('{\\rtf')) return 'rtf';
    if (/<\s*(html|body|div|p|span|table|br|font|b|i|u)\b/i.test(head)) return 'html';
    return 'text';
};

const escapeHtml = (s) => (s || '')
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

// OBX-5 değerini bölümlere ayırır. Format: "<içerik>^<indeks>" parçaları ~ ile ayrık
// (örn: "^1~^2~<base64>^3~<base64>^4" → 4 bölüm; 1-2 boş, 3-4 içerikli).
const parseSections = (parsed) => {
    const obx = parsed.segments.filter(s => s.name === 'OBX');
    const obxValues = [];
    obx.forEach(s => {
        const f = s.fields.find(x => x.key === 'OBX-5');
        if (f && f.value) obxValues.push(f.value);
    });

    const raw = (obxValues.length
        ? obxValues
        : parsed.segments.flatMap(s => s.fields.map(f => f.value).filter(Boolean)))
        .join('~');

    if (!raw) return [];

    return raw.split('~').map((tok, i) => {
        const m = tok.match(/^([\s\S]*)\^(\d+)$/);
        const content = m ? m[1] : tok;
        const index = m ? parseInt(m[2], 10) : (i + 1);

        const clean = (content || '').trim();
        let text = clean;
        let wasB64 = false;
        if (isBase64ish(clean)) {
            try { text = decodeBase64Utf8(clean); wasB64 = true; } catch { text = clean; }
        }
        return { index, text, kind: detectKind(text), wasB64 };
    });
};

export default function Hl7ReportContent({raw}) {
    const parsed = useMemo(() => parseHl7(raw), [raw]);
    const sections = useMemo(() => parseSections(parsed), [parsed]);

    const [view, setView] = useState('rendered');

    if (!sections.length || sections.every(s => !s.text.trim())) {
        return <Alert type={'info'} showIcon message={t('No report content found in this message.')} />;
    }

    const anyHtml = sections.some(s => s.kind === 'html');
    const anyRtf = sections.some(s => s.kind === 'rtf');
    const wasBase64 = sections.some(s => s.wasB64);

    // Her bölümü etiketli + ayraçlı bir blok olarak birleştir.
    const buildHtmlDoc = () => sections.map(sec => {
        const label = `${t('Part')} ${sec.index}`;
        const body = sec.text.trim()
            ? (sec.kind === 'html' ? sec.text : `<pre style="white-space:pre-wrap;margin:0">${escapeHtml(sec.text)}</pre>`)
            : `<i style="color:#bbb">(${t('empty')})</i>`;
        return `<section style="border-top:1px dashed #ccc;margin-top:10px;padding-top:6px">
            <div style="font-size:11px;color:#999;margin-bottom:4px">${label}</div>
            ${body}
        </section>`;
    }).join('');

    return (
        <div>
            <Radio.Group
                size={'small'}
                value={view}
                onChange={e => setView(e.target.value)}
                style={{ marginBottom: 8 }}
            >
                <Radio.Button value={'rendered'}>{t('Rendered')}</Radio.Button>
                <Radio.Button value={'source'}>{t('Source')}</Radio.Button>
            </Radio.Group>
            <Typography.Text type={'secondary'} style={{ marginLeft: 8, fontSize: 12 }}>
                {sections.length} {t('parts')}{wasBase64 ? ' · base64' : ''}
            </Typography.Text>

            {anyRtf && view === 'rendered' && (
                <Alert
                    type={'warning'}
                    showIcon
                    message={t('RTF content cannot be rendered in the browser. Showing the source below.')}
                    style={{ marginBottom: 8 }}
                />
            )}

            {view === 'rendered' && anyHtml && !anyRtf && (
                <iframe
                    title={'report'}
                    srcDoc={buildHtmlDoc()}
                    sandbox={''}
                    style={{ width: '100%', height: '62vh', border: '1px solid #eee', background: '#fff' }}
                />
            )}

            {(view === 'source' || anyRtf || (!anyHtml && view === 'rendered')) && (
                <div style={{ maxHeight: '62vh', overflow: 'auto', background: '#f6f6f6', padding: 8 }}>
                    {sections.map((sec, i) => (
                        <div key={i} style={{ borderTop: i ? '1px dashed #ccc' : 'none', marginTop: i ? 10 : 0, paddingTop: i ? 6 : 0 }}>
                            <div style={{ fontSize: 11, color: '#999', marginBottom: 4 }}>{t('Part')} {sec.index}</div>
                            <pre style={{ margin: 0, fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                {sec.text.trim() || `(${t('empty')})`}
                            </pre>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
